import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import FileDropper from '../../Common/FileDropper';
import { uploadResourceFile } from '../../../support/resourceFiles';
import { DocumentUploadAnswer } from '../../../support/assessments';

/**
 * Answer input for a `document_upload` question — a real file dropper.
 *
 * The bytes are stored as a generic resource file (the S3-backed `resource-file` path), and the
 * answer keeps only a reference (`resource_id` + name), never the bytes. One document per question:
 * a fresh upload replaces the previous reference. This is the non-Salesforce upload; the account
 * variant lives in `SalesforceAccountFileInput`.
 */

type Props = {
  value: DocumentUploadAnswer | null;
  onChange: (value: DocumentUploadAnswer | null) => void;
  disabled?: boolean;
};

const DocumentUploadInput: React.FC<Props> = ({ value, onChange, disabled }) => (
  <Box>
    {value?.file_name && (
      <Chip
        sx={{ mb: 1 }}
        label={value.file_name}
        onDelete={disabled ? undefined : () => onChange(null)}
        variant="outlined"
      />
    )}
    <FileDropper
      title="Upload a document"
      description="Drag & drop a file here, or click to select."
      disabled={disabled}
      upload={({ file, onProgress }) =>
        uploadResourceFile(file, { onProgress: (p) => onProgress?.(p.percent) })
      }
      onUploaded={(rec: any) =>
        onChange({ resource_id: rec?.id, file_name: rec?.file_name || rec?.name })
      }
    />
    <Typography variant="caption" color="text.secondary">
      The uploaded document is stored as a file resource; the answer records a reference to it.
    </Typography>
  </Box>
);

export default DocumentUploadInput;
